import React, { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import usePostApprovalPlaidLink from "../../../hooks/usePostApprovalPlaidLink";
import securityIcon from "../../../lib/images/post_approval/security.svg";
import OutlineButton from "../../shared/buttons/OutlineButton";
import PrimaryButton from "../../shared/buttons/PrimaryButton";
import PostApprovalAccountPicker from "./PostApprovalAccountPicker";
import PostApprovalPlaidProcessingView from "./PostApprovalPlaidProcessingView";

const value = (record, key) =>
  record && typeof record.get === "function" ? record.get(key) : undefined;

const errorValue = (error, key) =>
  error && typeof error.get === "function" ? error.get(key) : error?.[key];

const errorPresentation = (error, linkError) => {
  const reason = errorValue(error, "reason_code") || linkError?.code;
  const network = errorValue(error, "network");

  if (reason === "submission_conflict") {
    return {
      copy: "This bank connection was already submitted. Refresh status to continue.",
      action: "Refresh status",
      kind: "retryStatus",
    };
  }
  if (
    ["invalid_account", "attempt_not_link_ready", "invalid_request"].includes(
      reason
    )
  ) {
    return {
      copy: "We could not use that account. Reconnect your bank and try again.",
      action: "Reconnect",
      kind: "connect",
    };
  }
  if (
    network ||
    errorValue(error, "http_status") >= 500 ||
    ["invalid_response", "submission_unknown"].includes(reason)
  ) {
    return {
      copy: "We could not confirm whether your bank connection was submitted.",
      action: "Check status",
      kind: "refreshStatus",
    };
  }
  if (reason === "plaid_unavailable") {
    return {
      copy: "Bank connection is temporarily unavailable.",
      action: "Try again",
      kind: "connect",
    };
  }
  if (reason === "submit_not_started") {
    return {
      copy: "Your account is ready to submit.",
      action: "Submit",
      kind: "submit",
    };
  }
  if (reason) {
    return {
      copy:
        reason === "no_account_selected"
          ? "No bank account was selected."
          : "The bank connection did not finish.",
      action: "Try again",
      kind: "connect",
    };
  }
  return null;
};

const PostApprovalPlaidConnect = ({
  onAccepted,
  onTerminal,
  renderProcessing,
}) => {
  const history = useHistory();
  const plaid = usePostApprovalPlaidLink({ onAccepted });
  const terminalAttempt = useRef(null);
  const presentation = errorPresentation(plaid.error, plaid.linkError);
  const attemptId = value(plaid.attempt, "attempt_id");
  const status = value(plaid.attempt, "status");

  useEffect(() => {
    if (
      plaid.phase === "terminal" &&
      attemptId &&
      terminalAttempt.current !== attemptId
    ) {
      terminalAttempt.current = attemptId;
      onTerminal?.(plaid.attempt);
    }
  }, [attemptId, onTerminal, plaid.attempt, plaid.phase]);

  const runAction = () => {
    if (!presentation) return;
    if (presentation.kind === "retryStatus") plaid.retryStatus();
    if (presentation.kind === "refreshStatus") plaid.refreshStatus();
    if (presentation.kind === "connect") plaid.connect();
    if (
      presentation.kind === "submit" &&
      typeof plaid.retrySelectedAccount === "function"
    ) {
      plaid.retrySelectedAccount();
    }
  };

  if (
    status === "failed_soft" &&
    value(plaid.attempt, "account_ready") === false
  ) {
    return (
      <p role="status" aria-live="polite">
        We could not verify this account for continued processing.
      </p>
    );
  }

  if (plaid.phase === "terminal") return null;

  if (status === "processing") {
    if (plaid.pollingExhausted) {
      const unauthorized = errorValue(plaid.error, "http_status") === 401;
      return (
        <PostApprovalPlaidProcessingView
          presentation={unauthorized ? "auth_recovery" : "status_recovery"}
          busy={plaid.loading}
          onRetryStatus={() => {
            Promise.resolve(plaid.retryStatus()).catch(() => undefined);
          }}
          onReauthenticate={() => history.push("/init/existing_user")}
        />
      );
    }
    if (renderProcessing) {
      return typeof renderProcessing === "function"
        ? renderProcessing(plaid.attempt)
        : renderProcessing;
    }
    return (
      <p role="status" aria-live="polite">
        Your bank connection is processing.
      </p>
    );
  }

  if (presentation) {
    return (
      <div role="alert">
        <p>{presentation.copy}</p>
        <OutlineButton
          buttonColor="border-primary text-primary"
          buttonText={presentation.action}
          disabled={plaid.loading || plaid.connecting}
          onClick={runAction}
        />
      </div>
    );
  }

  if (plaid.pendingAccounts?.length) {
    return (
      <PostApprovalAccountPicker
        accounts={plaid.pendingAccounts}
        disabled={plaid.loading}
        onSubmit={plaid.chooseAccount}
      />
    );
  }

  return (
    <>
      <button
        type="button"
        className="post-approval-link-tile"
        disabled={plaid.connecting || plaid.loading}
        onClick={plaid.connect}
      >
        <span>
          <span className="block font-medium">
            Link Bank Account for More Benefits
          </span>
          <span className="block text-xs mt-1">
            Securely connect via Plaid instantly
          </span>
        </span>
        <span className="post-approval-fast">Fast and Secure</span>
        <span aria-hidden="true" className="text-lg text-tertiary-gray">
          ›
        </span>
      </button>
      <div className="post-approval-security">
        <img
          src={securityIcon}
          alt=""
          width="15"
          height="15"
          className="shrink-0"
        />
        <p>
          Your bank credentials are encrypted and never stored by Credit Key. We
          use bank-level security to protect your information.
        </p>
      </div>
      <PrimaryButton
        buttonColor="bg-primary-blue"
        buttonText="Link bank account"
        disabled={plaid.connecting || plaid.loading}
        isLoading={plaid.connecting || plaid.loading}
        onClick={plaid.connect}
      />
    </>
  );
};

export default PostApprovalPlaidConnect;
